import React, { useCallback, useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProvided,
  DropResult,
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import { DragIndicator, ArrowDropDown } from '@mui/icons-material';

import {
  Box,
  Chip,
  Menu,
  MenuItem,
  InputLabel,
  useMediaQuery,
} from "@mui/material";

//styles
import {
  Block,
  useStyles,
  DragField,
  FieldArrow,
  Fieldset,
} from "./styles";

//helpers
import { hasActiveItem } from "./helpers";

//hooks
import useDimensions from "../../../../../../customHooks/useDimensions";

//components
import ExpandedMenuItem from "./ExpandedMenuItem";

export interface IItemOption {
  id: string | number;
  value: string | string[];
  expanded: boolean;
}

const AFFILIATED_IDS = ['groupBySubAffiliate', 'groupByPlayer'];
const AFFILIATED_ORDER = ['groupByPlayer', 'groupBySubAffiliate'];

interface IGroupByProps {
  name: string;
  disabled?: boolean;
  value: IItemOption[];
  options: IItemOption[];
  setFilter: (props: any) => void;
  prefix?: string;
}

const GroupByField: React.FC<IGroupByProps> = ({
  name,
  value,
  options,
  setFilter,
  prefix= '',
  disabled = false,
}): JSX.Element => {
  const styles = useStyles();
  const { t } = useTranslation();
  const mobile = useMediaQuery('(max-width:767px)');

  const fieldRef = useRef(null);
  const fieldSize = useDimensions(fieldRef);

  const [dropdownWidth, setDropdownWidth] = useState(fieldSize.width | 0);

  const [selected, setSelected] = useState<IItemOption[]>(value);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  useEffect(() => {
    setDropdownWidth(fieldSize.width);
  }, [fieldSize]);

  const getAffiliatedLabel = (items: IItemOption[]): string => {
    const affiliatedSelected = items.filter((el) => AFFILIATED_IDS.includes(el.id as string));
    const sortedLabels = AFFILIATED_ORDER
      .filter((id) => affiliatedSelected.some((el) => el.id === id))
      .map((id) => {
        const item = affiliatedSelected.find((el) => el.id === id);
        return prefix ? t(`${prefix}${item?.value}`) : item?.value;
      });
    return sortedLabels.join(' - ');
  };

  const displayItems = useMemo(() => {
    const affiliatedItems = selected.filter((el) => AFFILIATED_IDS.includes(el.id as string));
    const otherItems = selected.filter((el) => !AFFILIATED_IDS.includes(el.id as string));

    if (affiliatedItems.length === 0) return otherItems;

    const combinedItem: IItemOption = {
      id: 'groupBySubAffiliate',
      value: 'combinedAffiliate',
      expanded: false,
    };

    const result: IItemOption[] = [];
    let affiliatedInserted = false;

    for (let i = 0; i < selected.length; i++) {
      if (AFFILIATED_IDS.includes(selected[i].id as string)) {
        if (!affiliatedInserted) {
          result.push(combinedItem);
          affiliatedInserted = true;
        }
      } else {
        result.push(selected[i]);
      }
    }

    return result;
  }, [selected]);

  const handleDelete = useCallback((element: any) => {
    if (AFFILIATED_IDS.includes(element.id as string)) {
      const newSelectedValue = selected.filter((item: any) => !AFFILIATED_IDS.includes(item.id));
      setFilter(newSelectedValue);
    } else {
      const newSelectedValue = selected.filter((item: any) => item.id !== element.id);
      setFilter(newSelectedValue);
    }
  }, [value, selected]);

  const handleChipClick = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleClick = useCallback((ref: any) => {
    setDropdownWidth(ref.current?.offsetWidth);
    setAnchorEl(ref.current);
  }, [setAnchorEl]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  },[setAnchorEl]);

  const onDragEnd = (result: DropResult) => {
    const {destination, source} = result;

    if (!destination) {
      return;
    }

    const newDisplayItems: IItemOption[] = Array.from(displayItems);
    const [removed] = newDisplayItems.splice(source.index, 1);
    newDisplayItems.splice(destination.index, 0, removed);

    const newSelected: IItemOption[] = [];
    for (const displayItem of newDisplayItems) {
      if (displayItem.id === 'groupBySubAffiliate' && displayItem.value === 'combinedAffiliate') {
        const affiliatedInOrder = AFFILIATED_ORDER
          .filter((id) => selected.some((el) => el.id === id))
          .map((id) => selected.find((el) => el.id === id)!);
        newSelected.push(...affiliatedInOrder);
      } else {
        newSelected.push(displayItem);
      }
    }

    setFilter(newSelected);
  };

  const ensureAffiliatedOrder = (items: IItemOption[]): IItemOption[] => {
    const affiliatedItems = items.filter((el) => AFFILIATED_IDS.includes(el.id as string));
    const otherItems: IItemOption[] = [];
    let firstAffiliatedIndex = -1;

    for (let i = 0; i < items.length; i++) {
      if (AFFILIATED_IDS.includes(items[i].id as string)) {
        if (firstAffiliatedIndex === -1) firstAffiliatedIndex = otherItems.length;
      } else {
        otherItems.push(items[i]);
      }
    }

    const sortedAffiliated = AFFILIATED_ORDER
      .filter((id) => affiliatedItems.some((el) => el.id === id))
      .map((id) => affiliatedItems.find((el) => el.id === id)!);

    if (firstAffiliatedIndex === -1) return otherItems;

    const result = [...otherItems];
    result.splice(firstAffiliatedIndex, 0, ...sortedAffiliated);
    return result;
  };

  const handleChangeOption = (item: IItemOption) => {
    const hasItem = !!selected.filter((el: IItemOption) => el.id === item.id).length;

    if (hasItem) {
      if (item.id === 'groupBySubAffiliate') {
        setFilter((prevState: IItemOption[]) => {
          return prevState.filter((el: IItemOption) => !AFFILIATED_IDS.includes(el.id as string));
        });
      } else {
        setFilter((prevState: IItemOption[]) => {
          return prevState.filter((el: IItemOption) => el.id !== item.id);
        });
      }
    } else {
      if (item.id === 'groupByPlayer') {
        setFilter((prevState: IItemOption[]) => {
          let newState = prevState.filter((el) => !AFFILIATED_IDS.includes(el.id as string));

          const subAffiliateOption = options.find((el) => el.id === 'groupBySubAffiliate');

          const affiliatedGroup: IItemOption[] = [];
          affiliatedGroup.push(item);
          if (subAffiliateOption) affiliatedGroup.push(subAffiliateOption);

          newState.push(...affiliatedGroup);
          return ensureAffiliatedOrder(newState);
        });
      } else if (item.id === 'groupBySubAffiliate') {
        setFilter((prevState: IItemOption[]) => {
          let newState = [...prevState];
          newState.push(item);
          return ensureAffiliatedOrder(newState);
        });
      } else {
        setFilter((prevState: IItemOption[]) => {
          return [...prevState, item];
        });
      }
    }
  }

  return (
    <Block>
      <InputLabel className={`${styles.inputLabel} ${!!selected.length || open ? 'active' : ''}`}>
        {t("admin.reports.filter.group_by")}
      </InputLabel>
      <DragField
        isOpen={open}
        ref={fieldRef}
        id="group-by-menu"
        aria-controls={open ? 'basic-group-by-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Fieldset
          component="fieldset"
          selected={!!selected.length}
          isOpen={open}
          onClick={() => handleClick(fieldRef)}
        >
          <legend>
            <span>{t("admin.reports.filter.group_by")}</span>
          </legend>
        </Fieldset>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable" ignoreContainerClipping={true} direction={mobile ? 'vertical' : 'horizontal'}>
            {(provided: DroppableProvided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="droppable-wrap"
              >
                {displayItems.map((item: IItemOption, index) => {
                  const isCombinedChip = item.id === 'groupBySubAffiliate' && item.value === 'combinedAffiliate';
                  const chipLabel = isCombinedChip
                    ? getAffiliatedLabel(selected)
                    : (prefix ? t(`${prefix}${item.value}`) : item.value);

                  return (
                    <Draggable
                      key={item.id}
                      draggableId={`${item.id}`}
                      index={index}
                    >
                      {(providedDraggable: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                        <div
                          ref={providedDraggable.innerRef}
                          {...providedDraggable.draggableProps}
                          {...providedDraggable.dragHandleProps}
                          className="chipWrapper"
                          onClick={e => {
                            e.stopPropagation()
                          }}
                        >
                          <Chip
                            className={`${styles.chip} ${snapshot.isDragging ? 'isDragging' : ''} `}
                            size="small"
                            label={chipLabel}
                            onClick={e => handleChipClick(e)}
                            onDelete={() => handleDelete(item)}
                            icon={<DragIndicator className={styles.drag} />}
                          />
                        </div>
                      )}
                    </Draggable>
                  )
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <FieldArrow isOpen={open}>
          <ArrowDropDown />
        </FieldArrow>
        <Box
          className={'handleLayer'}
          onClick={() => handleClick(fieldRef)}
        />
      </DragField>

      <Menu
        id="basic-group-by-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': 'group-by-menu' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        PaperProps={{
          style: {
            width: "100%",
            maxWidth: `${dropdownWidth}px`,
          }
        }}
      >
        {
          options.map((item) => {
            if (!item.expanded) {
              return (
                <MenuItem
                  sx={{
                    minHeight: '36px',
                  }}
                  key={item.id}
                  selected={hasActiveItem(item.id, selected)}
                  onClick={() => handleChangeOption(item)}
                >
                  {prefix ? t(`${prefix}${item.value}`) : item.value}
                </MenuItem>
              )
            } else {
              return (
                <ExpandedMenuItem
                  key={item.id}
                  id={item.id}
                  name={name}
                  selected={selected}
                  options={item.value}
                  setFilter={setFilter}
                  prefix={prefix}
                />
              )
            }
          })
        }
      </Menu>
    </Block>
  );
};

export default GroupByField;
